import { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  Chip,
  FormControlLabel,
} from '@mui/material';
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { CaseStatus } from '@bandido/shared/constants';
import {
  UserNotificationTrigger,
  UserNotificationTriggerDeliveryCapabilities,
  UserNotificationTriggerFormData,
} from '../../types';
import { ClientResponseDto, UserListResponseDto } from '@bandido/shared/dtos';
import { useGetAllClients, useGetClientById } from '@frontend/features/clients/queries';
import { useGetAllUsers, useGetUserById } from '@frontend/features/users/queries';
import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';

interface TriggerFormProps {
  trigger?: UserNotificationTrigger;
  deliveryCapabilities?: UserNotificationTriggerDeliveryCapabilities;
  onSubmit: (data: UserNotificationTriggerFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface WorkflowRow {
  workflowId: string | null;
  stepIds: string[];
}

interface WorkflowRowErrors {
  workflowId?: string;
  stepIds?: string;
}

const splitName = (name?: string | null) => {
  if (!name) {
    return { firstName: '', lastName: '' };
  }
  const parts = name.trim().split(/\s+/);
  return {
    firstName: parts.shift() ?? '',
    lastName: parts.join(' '),
  };
};

type ClientOption = Pick<ClientResponseDto, 'id' | 'firstName' | 'lastName' | 'email'> &
  Partial<ClientResponseDto>;
type AgentOption = Pick<UserListResponseDto, 'id' | 'firstName' | 'lastName' | 'email'> &
  Partial<UserListResponseDto>;

export const TriggerForm = ({
  trigger,
  deliveryCapabilities,
  onSubmit,
  onCancel,
  isLoading = false,
}: TriggerFormProps) => {
  const { t } = useTranslation();

  // Estados para búsqueda en autocompletes
  const [clientSearch, setClientSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');

  // Estados para formulario y selecciones
  const [formData, setFormData] = useState<UserNotificationTriggerFormData>({
    clientId: trigger?.clientId || undefined,
    agentId: trigger?.agentId || undefined,
    status: trigger?.status || undefined,
    assigneeScope: trigger?.assigneeScope ?? 'any',
    sendInApp: trigger?.sendInApp ?? true,
    sendEmail: trigger?.sendEmail ?? true,
    sendGoogleChat: trigger?.sendGoogleChat ?? false,
    quietHoursStart: trigger?.quietHoursStart ?? null,
    quietHoursEnd: trigger?.quietHoursEnd ?? null,
    timezone: trigger?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    caseId: trigger?.caseId || undefined,
  });

  const [rows, setRows] = useState<WorkflowRow[]>([{ workflowId: null, stepIds: [] }]);
  const [rowErrors, setRowErrors] = useState<WorkflowRowErrors[]>([]);
  const [statusError, setStatusError] = useState<string | null>(null);
  const isRowComplete = (row: WorkflowRow) => Boolean(row.workflowId) && row.stepIds.length > 0;
  const canAddRow = rows.length > 0 ? isRowComplete(rows[rows.length - 1]) : false;

  // Queries para obtener datos reales
  const { data: clientList } = useGetAllClients({
    pageSize: 50,
    currentPage: 1,
    search: clientSearch || '',
    clientName: '',
    sort: 'firstName',
    order: 'ASC',
  });

  const { data: agentList } = useGetAllUsers({
    pageSize: 50,
    currentPage: 1,
    search: agentSearch || '',
  });

  const { data: clientById } = useGetClientById({ id: formData.clientId });
  const { data: agentById } = useGetUserById({ id: formData.agentId });

  const { data: workflows } = useGetAllWorkflowsQuery({
    pageSize: 50,
    currentPage: 1,
    search: '',
  });

  const { data: steps } = useGetStepsByManyWorkflowIds({
    search: '',
    workflowIds: rows
      .map((row) => row.workflowId)
      .filter((workflowId): workflowId is string =>
        Boolean(workflowId && workflowId.length > 0),
      ),
  });

  const stepsByWorkflowId = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string }>>();
    if (!steps) return map;
    steps.forEach((step) => {
      if (!step.workflowId) return;
      if (!map.has(step.workflowId)) {
        map.set(step.workflowId, []);
      }
      map.get(step.workflowId)!.push({ id: step.id, name: step.name ?? '' });
    });
    return map;
  }, [steps]);

  const clientOptions = useMemo<ClientOption[]>(
    () => (clientList?.items || []).filter((client) => Boolean(client?.id)),
    [clientList?.items],
  );
  const agentOptions = useMemo<AgentOption[]>(
    () => (agentList?.items || []).filter((agent) => Boolean(agent?.id)),
    [agentList?.items],
  );

  const getClientLabel = (option: ClientOption) => {
    const first = option.firstName ?? '';
    const last = option.lastName ?? '';
    const name = `${first} ${last}`.trim();
    return name || option.email || option.id || '';
  };

  const getAgentLabel = (option: AgentOption) => {
    const first = option.firstName ?? '';
    const last = option.lastName ?? '';
    const name = `${first} ${last}`.trim();
    return name || option.email || option.id || '';
  };

  useEffect(() => {
    const workflowSteps = trigger?.workflowSteps;
    const nextRows: WorkflowRow[] =
      workflowSteps && workflowSteps.length > 0
        ? workflowSteps.map((workflow) => ({
            workflowId: workflow.workflowId,
            stepIds: workflow.stepIds,
          }))
        : trigger?.workflowId && trigger?.stepId
          ? [{ workflowId: trigger.workflowId, stepIds: [trigger.stepId] }]
          : [{ workflowId: null, stepIds: [] }];

    setRows(nextRows);
    setRowErrors([]);
    setStatusError(null);
    setFormData({
      clientId: trigger?.clientId || undefined,
      agentId: trigger?.agentId || undefined,
      status: trigger?.status || undefined,
      assigneeScope: trigger?.assigneeScope ?? 'any',
      sendInApp: trigger?.sendInApp ?? true,
      sendEmail: trigger?.sendEmail ?? true,
      sendGoogleChat: trigger?.sendGoogleChat ?? false,
      quietHoursStart: trigger?.quietHoursStart ?? null,
      quietHoursEnd: trigger?.quietHoursEnd ?? null,
      timezone: trigger?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      caseId: trigger?.caseId || undefined,
    });
  }, [trigger?.id]);

  const fallbackClientOption = useMemo<ClientOption | null>(() => {
    if (!formData.clientId) return null;
    if (clientById && clientById.id === formData.clientId) {
      return clientById;
    }
    if (trigger?.clientId === formData.clientId && trigger?.clientName) {
      const { firstName, lastName } = splitName(trigger.clientName);
      return {
        id: trigger.clientId,
        firstName,
        lastName,
        email: '',
      };
    }
    return null;
  }, [clientById, formData.clientId, trigger?.clientId, trigger?.clientName]);

  const fallbackAgentOption = useMemo<AgentOption | null>(() => {
    if (!formData.agentId) return null;
    if (agentById && agentById.id === formData.agentId) {
      return {
        id: agentById.id,
        firstName: agentById.firstName ?? '',
        lastName: agentById.lastName ?? '',
        email: agentById.email ?? '',
      };
    }
    if (trigger?.agentId === formData.agentId && trigger?.agentName) {
      const { firstName, lastName } = splitName(trigger.agentName);
      return {
        id: trigger.agentId,
        firstName,
        lastName,
        email: '',
      };
    }
    return null;
  }, [agentById, formData.agentId, trigger?.agentId, trigger?.agentName]);

  const mergedClientOptions = useMemo<ClientOption[]>(() => {
    if (!fallbackClientOption) return clientOptions;
    const exists = clientOptions.some((option) => option.id === fallbackClientOption.id);
    return exists ? clientOptions : [...clientOptions, fallbackClientOption];
  }, [clientOptions, fallbackClientOption]);

  const mergedAgentOptions = useMemo<AgentOption[]>(() => {
    if (!fallbackAgentOption) return agentOptions;
    const exists = agentOptions.some((option) => option.id === fallbackAgentOption.id);
    return exists ? agentOptions : [...agentOptions, fallbackAgentOption];
  }, [agentOptions, fallbackAgentOption]);

  const selectedClientOption = useMemo<ClientOption | null>(() => {
    if (!formData.clientId) return null;
    return mergedClientOptions.find((option) => option.id === formData.clientId) ?? null;
  }, [mergedClientOptions, formData.clientId]);

  const selectedAgentOption = useMemo<AgentOption | null>(() => {
    if (!formData.agentId) return null;
    return mergedAgentOptions.find((option) => option.id === formData.agentId) ?? null;
  }, [mergedAgentOptions, formData.agentId]);

  const updateRow = (index: number, updates: Partial<WorkflowRow>) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...updates } : row,
      ),
    );
    setRowErrors([]);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, { workflowId: null, stepIds: [] }]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_row, rowIndex) => rowIndex !== index));
    setRowErrors([]);
  };

  const handleSubmit = () => {
    const nextRowErrors = rows.map((row) => ({
      workflowId: row.workflowId ? undefined : t('userNotificationTriggers.validation.workflowRequired'),
      stepIds: row.stepIds.length > 0 ? undefined : t('userNotificationTriggers.validation.stepsRequired'),
    }));
    const nextStatusError = formData.status
      ? null
      : t('userNotificationTriggers.validation.statusRequired');

    setRowErrors(nextRowErrors);
    setStatusError(nextStatusError);

    if (nextStatusError || nextRowErrors.some((error) => error.workflowId || error.stepIds)) {
      return;
    }

    const stepsByWorkflow = new Map<string, Set<string>>();
    rows.forEach((row) => {
      if (!row.workflowId) return;
      const workflowId = row.workflowId;
      if (!stepsByWorkflow.has(workflowId)) {
        stepsByWorkflow.set(workflowId, new Set());
      }
      row.stepIds.forEach((stepId) => stepsByWorkflow.get(workflowId)!.add(stepId));
    });

    const workflowSteps = Array.from(stepsByWorkflow.entries()).map(([workflowId, stepIds]) => ({
      workflowId,
      stepIds: Array.from(stepIds),
    }));

    onSubmit({
      ...formData,
      sendEmail: !trigger && emailDisabled ? false : formData.sendEmail,
      sendGoogleChat:
        !trigger && googleChatDisabled ? false : formData.sendGoogleChat,
      workflowSteps,
    });
  };

  const statusList = [
    { id: CaseStatus.canceled, name: t(`cases.status.${CaseStatus.canceled}`) },
    { id: CaseStatus.done, name: t(`cases.status.${CaseStatus.done}`) },
    { id: CaseStatus.inProgress, name: t(`cases.status.${CaseStatus.inProgress}`) },
    { id: CaseStatus.needsAgentAction, name: t(`cases.status.${CaseStatus.needsAgentAction}`) },
    { id: CaseStatus.needsClientAction, name: t(`cases.status.${CaseStatus.needsClientAction}`) },
    { id: CaseStatus.open, name: t(`cases.status.${CaseStatus.open}`) },
  ];

  const emailDisabled = deliveryCapabilities?.emailConfigured === false;
  const googleChatDisabled =
    deliveryCapabilities?.googleChatConfigured === false;

  useEffect(() => {
    if (trigger) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      sendEmail: emailDisabled ? false : prev.sendEmail,
      sendGoogleChat: googleChatDisabled ? false : prev.sendGoogleChat,
    }));
  }, [trigger, emailDisabled, googleChatDisabled]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('userNotificationTriggers.formDescription')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.clientOptional')}
            </Typography>
            {formData.clientId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, clientId: undefined }));
                  setClientSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Autocomplete
            fullWidth
            size="small"
            options={mergedClientOptions}
            getOptionLabel={getClientLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedClientOption}
            onChange={(_, value) => {
              setFormData(prev => ({ 
                ...prev, 
                clientId: value?.id 
              }));
              // Limpiar búsqueda cuando se selecciona una opción
              if (!value) setClientSearch('');
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setClientSearch(value);
              }
              if (reason === 'clear') {
                setClientSearch('');
              }
            }}
            noOptionsText={t('common.noData')}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('userNotificationTriggers.selectClient')} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('userNotificationTriggers.assigneeScope.label')}
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.assigneeScope ?? 'any'}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  assigneeScope: event.target.value as UserNotificationTriggerFormData['assigneeScope'],
                }))
              }
            >
              {(['any', 'mine', 'team', 'unassigned'] as const).map((scope) => (
                <MenuItem key={scope} value={scope}>
                  {t(`userNotificationTriggers.assigneeScope.${scope}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            {t('userNotificationTriggers.quietHours.label')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label={t('userNotificationTriggers.quietHours.start')}
                value={formData.quietHoursStart ?? ''}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    quietHoursStart: event.target.value || null,
                    quietHoursEnd: event.target.value
                      ? prev.quietHoursEnd ?? '07:00'
                      : null,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label={t('userNotificationTriggers.quietHours.end')}
                disabled={!formData.quietHoursStart}
                value={formData.quietHoursEnd ?? ''}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    quietHoursEnd: event.target.value || null,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('userNotificationTriggers.quietHours.timezone')}
                value={formData.timezone ?? ''}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, timezone: event.target.value }))
                }
                inputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary">
            {t('userNotificationTriggers.quietHours.help')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.agentOptional')}
            </Typography>
            {formData.agentId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, agentId: undefined }));
                  setAgentSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Autocomplete
            fullWidth
            size="small"
            options={mergedAgentOptions}
            getOptionLabel={getAgentLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedAgentOption}
            onChange={(_, value) => {
              setFormData(prev => ({ 
                ...prev, 
                agentId: value?.id 
              }));
              // Limpiar búsqueda cuando se selecciona una opción
              if (!value) setAgentSearch('');
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setAgentSearch(value);
              }
              if (reason === 'clear') {
                setAgentSearch('');
              }
            }}
            noOptionsText={t('common.noData')}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('userNotificationTriggers.selectAgent')} />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" flexDirection="column" gap={2}>
            {rows.map((row, index) => {
              const workflowOptions = workflows?.items || [];
              const stepsOptions =
                row.workflowId && stepsByWorkflowId.has(row.workflowId)
                  ? stepsByWorkflowId.get(row.workflowId) || []
                  : [];
              const currentWorkflow = workflowOptions.find(
                (workflow) => workflow.id === row.workflowId,
              ) || null;

              const selectedSteps = stepsOptions.filter((step) =>
                row.stepIds.includes(step.id),
              );

              return (
                <Box key={`${row.workflowId ?? 'row'}-${index}`}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
                    {index > 0 && (
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveRow(index)}
                        sx={{ minWidth: 'auto', p: 0 }}
                      >
                        {t('userNotificationTriggers.removeWorkflow')}
                      </Button>
                    )}
                  </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 4,
                    alignItems: 'start',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
                      <Typography variant="subtitle2" gutterBottom>
                        {t('userNotificationTriggers.workflow')}
                      </Typography>
                      {row.workflowId && (
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          onClick={() => {
                            updateRow(index, { workflowId: null, stepIds: [] });
                          }}
                          sx={{ minWidth: 'auto', p: 0 }}
                        >
                          {t('filters.clear')}
                        </Button>
                      )}
                    </Box>

                    <Autocomplete
                      fullWidth
                      size="small"
                      options={workflowOptions}
                      getOptionLabel={(option) => option.name ?? (option as { label?: string }).label ?? ''}
                      value={currentWorkflow}
                      onChange={(_event, value) => {
                        updateRow(index, {
                          workflowId: value?.id ?? null,
                          stepIds: [],
                        });
                      }}
                      noOptionsText={t('common.noData')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(rowErrors[index]?.workflowId)}
                          helperText={rowErrors[index]?.workflowId}
                          placeholder={t('userNotificationTriggers.selectWorkflow')}
                        />
                      )}
                    />
                  </Box>

                  {/* Steps column */}
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
                      <Typography variant="subtitle2" gutterBottom>
                        {t('userNotificationTriggers.steps')}
                      </Typography>
                      {row.stepIds.length > 0 && (
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          onClick={() => {
                            updateRow(index, { stepIds: [] });
                          }}
                          sx={{ minWidth: 'auto', p: 0 }}
                        >
                          {t('filters.clear')}
                        </Button>
                      )}
                    </Box>

                    <Autocomplete
                      fullWidth
                      size="small"
                      multiple
                      disabled={!row.workflowId}
                      options={stepsOptions}
                      getOptionLabel={(option) => option.name}
                      value={selectedSteps}
                      onChange={(_event, value) => updateRow(index, { stepIds: value.map((step) => step.id) })}
                      noOptionsText={t('common.noData')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(rowErrors[index]?.stepIds)}
                          helperText={rowErrors[index]?.stepIds}
                          placeholder={
                            row.workflowId
                              ? t('userNotificationTriggers.selectStep')
                              : t('userNotificationTriggers.selectWorkflowFirst')
                          }
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, chipIndex) => (
                          <Chip
                            {...getTagProps({ index: chipIndex })}
                            key={option.id}
                            label={option.name}
                            size="small"
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.id}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlank fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            sx={{ mr: 1 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                    />
                  </Box>
                </Box>


                </Box>
              );
            })}

            <Box>
              <Button
                variant="text"
                onClick={handleAddRow}
                disabled={!canAddRow}
                sx={{ p: 0 }}
              >
                {t('userNotificationTriggers.addWorkflow')}
              </Button>
              {!canAddRow && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {t('userNotificationTriggers.addWorkflowHint')}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.status')}
            </Typography>
            {formData.status && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, status: undefined }));
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <FormControl fullWidth size="small">
            <Select
              value={formData.status || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value || undefined }))}
              displayEmpty
              error={Boolean(statusError)}
            >
              <MenuItem value="">
                <em>{t('userNotificationTriggers.selectStatus')}</em>
              </MenuItem>
              {statusList.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
            {statusError && (
              <Typography variant="caption" color="error" mt={0.5}>
                {statusError}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.specificCase')}
            </Typography>
            {formData.caseId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, caseId: undefined }));
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder={t('userNotificationTriggers.caseIdPlaceholder')}
            value={formData.caseId || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              caseId: e.target.value ? Number(e.target.value) : undefined 
            }))}
          />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="subtitle2">
              {t('userNotificationTriggers.deliveryChannels')}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formData.sendInApp ?? true}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        sendInApp: event.target.checked,
                      }))
                    }
                  />
                )}
                label={t('userNotificationTriggers.channels.inApp')}
              />

              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={formData.sendEmail ?? true}
                      disabled={emailDisabled}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          sendEmail: event.target.checked,
                        }))
                      }
                    />
                  )}
                  label={t('userNotificationTriggers.channels.email')}
                />
                {emailDisabled && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 4.5, mt: -0.5 }}
                  >
                    {t('userNotificationTriggers.channelAvailability.emailUnavailable')}
                  </Typography>
                )}
              </Box>

              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={formData.sendGoogleChat ?? false}
                      disabled={googleChatDisabled}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          sendGoogleChat: event.target.checked,
                        }))
                      }
                    />
                  )}
                  label={t('userNotificationTriggers.channels.googleChat')}
                />
                {googleChatDisabled && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 4.5, mt: -0.5 }}
                  >
                    {t('userNotificationTriggers.channelAvailability.googleChatUnavailable')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary">
              {t('userNotificationTriggers.deliveryChannelsHelp')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button variant="outlined" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit}
        >
          {trigger ? t('common.update') : t('common.actions.add')}
        </LoadingButton>
      </Box>
    </Box>
  );
};
